import { useState, useEffect, useRef, useCallback } from 'react';
import GoBoard, { BoardMark, MarkType } from '../GoBoard/GoBoard';
import { GoGame, StoneColor, Coordinate, sgfUtils, estimateScore, findDeadStones } from '@go-platform/go-engine';
import { SERVER_URL } from '../../api';
import './Review.css';

const API_URL = SERVER_URL;

interface GameListItem {
  filename: string;
  roomCode: string;
  black: string;
  white: string;
  result: string;
  reason: string;
  boardSize: number;
  movesCount: number;
  endedAt: string;
  savedAt?: string;
}

interface ParsedMove {
  color: StoneColor;
  coord: Coordinate | null;
}

type Size = 9 | 13 | 19;
type ReviewMode = 'replay' | 'try' | 'mark';

function cloneStones(stones: StoneColor[][]): StoneColor[][] {
  return stones.map((row) => [...row]);
}

function makeSettings(boardSize: number, komi: number) {
  return {
    boardSize: boardSize as Size,
    handicap: 0,
    komi,
    timeSettings: { type: 'byoyomi' as const, mainTime: 1800, byoyomiTime: 30, byoyomiPeriods: 3 },
  };
}

function colorText(c: StoneColor): string {
  return c === StoneColor.BLACK ? '黑' : '白';
}

interface ReviewProps {
  onBack: () => void;
}

export default function Review({ onBack }: ReviewProps) {
  // 列表
  const [games, setGames] = useState<GameListItem[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState<GameListItem | null>(null);

  // 复盘数据
  const [snapshots, setSnapshots] = useState<StoneColor[][][]>([]);
  const [moves, setMoves] = useState<ParsedMove[]>([]);
  const [boardSize, setBoardSize] = useState<Size>(19);
  const [komi, setKomi] = useState(7.5);
  const [currentIndex, setCurrentIndex] = useState(0);

  // 模式
  const [mode, setMode] = useState<ReviewMode>('replay');
  const [branchBoard, setBranchBoard] = useState<StoneColor[][] | null>(null);
  const [branchLastMove, setBranchLastMove] = useState<Coordinate | null>(null);
  const [tryMsg, setTryMsg] = useState('');
  const branchGameRef = useRef<GoGame | null>(null);

  // 标记
  const [marks, setMarks] = useState<BoardMark[]>([]);
  const [markType, setMarkType] = useState<MarkType>('circle');
  const [markLetter, setMarkLetter] = useState('A');

  // 死子标记（终局数子前手动标记，修正胜负）
  const [deadStones, setDeadStones] = useState<Coordinate[]>([]);
  const [deadMode, setDeadMode] = useState(false);

  // 形势判断
  const [score, setScore] = useState<{ black: number; white: number } | null>(null);

  const loadList = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/games`);
      if (!res.ok) throw new Error('HTTP ' + res.status);
      setGames(await res.json());
    } catch (e: any) {
      setError('加载棋谱列表失败: ' + (e?.message || e));
      setGames([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadList();
  }, [loadList]);

  const openGame = async (item: GameListItem) => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/games/${encodeURIComponent(item.filename)}`);
      if (!res.ok) throw new Error('HTTP ' + res.status);
      const sgf = await res.text();
      const parsed = sgfUtils.createGameFromSGF(sgf);

      const game = new GoGame(makeSettings(parsed.boardSize, parsed.komi));
      game.start();
      const snaps: StoneColor[][][] = [cloneStones(game.getBoardState().stones)];
      for (const m of parsed.moves) {
        try {
          game.playMove(m.coord, m.color);
        } catch {
          // 非法步跳过，快照保持上一步局面
        }
        snaps.push(cloneStones(game.getBoardState().stones));
      }

      setSnapshots(snaps);
      setMoves(parsed.moves);
      setBoardSize(parsed.boardSize as Size);
      setKomi(parsed.komi);
      setCurrentIndex(0);
      setSelected(item);
      setMode('replay');
      setBranchBoard(null);
      setBranchLastMove(null);
      setTryMsg('');
      setDeadStones([]);
      setDeadMode(false);
      // 读回已存档的讲解标记
      setMarks(
        sgfUtils.extractMarksFromSGF(sgf).map((m) => ({
          coord: m.coord,
          type: m.type,
          letter: m.letter,
        }))
      );
      setScore(null);
    } catch (e: any) {
      setError('加载棋谱失败: ' + (e?.message || e));
    } finally {
      setLoading(false);
    }
  };

  const backToList = () => {
    setSelected(null);
    setSnapshots([]);
    setMoves([]);
    setCurrentIndex(0);
    setMode('replay');
    setBranchBoard(null);
    setMarks([]);
    setDeadStones([]);
    setDeadMode(false);
    setScore(null);
    setError('');
  };

  // 跳转（回主线：清空试下状态）
  const goto = (idx: number) => {
    branchGameRef.current = null;
    setBranchBoard(null);
    setBranchLastMove(null);
    setMode('replay');
    setTryMsg('');
    setDeadMode(false);
    setScore(null);
    const clamped = Math.max(0, Math.min(snapshots.length - 1, idx));
    setCurrentIndex(clamped);
  };

  // 试下
  const enterTry = () => {
    const game = new GoGame(makeSettings(boardSize, komi));
    game.start();
    for (let i = 0; i < currentIndex && i < moves.length; i++) {
      try {
        game.playMove(moves[i].coord, moves[i].color);
      } catch {
        // ignore
      }
    }
    branchGameRef.current = game;
    setBranchBoard(cloneStones(snapshots[currentIndex] || []));
    setBranchLastMove(currentIndex > 0 ? moves[currentIndex - 1].coord : null);
    setMode('try');
    setTryMsg('');
    setScore(null);
  };

  const exitTry = () => {
    branchGameRef.current = null;
    setBranchBoard(null);
    setBranchLastMove(null);
    setMode('replay');
    setTryMsg('');
  };

  // 落子 / 标记
  const handleBoardClick = (coord: Coordinate) => {
    if (deadMode) {
      const idx = deadStones.findIndex((d) => d.x === coord.x && d.y === coord.y);
      if (idx >= 0) {
        setDeadStones(deadStones.filter((_, i) => i !== idx));
      } else {
        setDeadStones([...deadStones, coord]);
      }
      return;
    }
    if (mode === 'try') {
      const game = branchGameRef.current;
      if (!game) return;
      try {
        game.playMove(coord, game.getCurrentPlayer());
        setBranchBoard(cloneStones(game.getBoardState().stones));
        setBranchLastMove(coord);
        setTryMsg('');
      } catch (e: any) {
        setTryMsg('非法着法: ' + (e?.message || e));
      }
    } else if (mode === 'mark') {
      const idx = marks.findIndex((m) => m.coord.x === coord.x && m.coord.y === coord.y);
      if (idx >= 0) {
        setMarks(marks.filter((_, i) => i !== idx));
      } else {
        setMarks([
          ...marks,
          { coord, type: markType, letter: markType === 'letter' ? markLetter : undefined },
        ]);
      }
    }
  };

  const handleScore = () => {
    const board = branchBoard ?? snapshots[currentIndex];
    if (!board || board.length === 0) return;
    setScore(estimateScore(board, deadStones));
  };

  // 讲解标记写回棋谱（存档，下次打开还在）
  const saveMarks = async () => {
    if (!selected) return;
    try {
      const res = await fetch(`${API_URL}/api/games/${encodeURIComponent(selected.filename)}/marks`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ marks }),
      });
      if (!res.ok) throw new Error('HTTP ' + res.status);
      alert('标记已保存到棋谱');
    } catch (e: any) {
      alert('保存失败: ' + (e?.message || e));
    }
  };

  // 自动识别死子（粗略启发式）：被对方完全围死的孤棋自动标为死子
  const autoMarkDead = () => {
    const board = branchBoard ?? snapshots[currentIndex];
    if (!board || board.length === 0) return;
    const found = findDeadStones(board);
    setDeadStones(found);
    if (found.length === 0) {
      alert('未自动识别到死子：盘面可能已无可提死子，或存在单眼假活/对杀等复杂死活，需人工标记。');
    }
  };

  const displayBoard = branchBoard ?? (snapshots[currentIndex] ?? []);
  const totalMoves = moves.length;
  const lastColor = currentIndex > 0 ? colorText(moves[currentIndex - 1].color) : null;
  const nextColor = currentIndex < totalMoves ? colorText(moves[currentIndex].color) : null;
  const lastMove =
    mode === 'try' && branchBoard
      ? branchLastMove
      : currentIndex > 0
        ? moves[currentIndex - 1].coord
        : null;

  const modeLabel: Record<ReviewMode, string> = {
    replay: '回放',
    try: '试下中',
    mark: '标记中',
  };

  return (
    <div className="review">
      {!selected ? (
        <div className="review-list-panel">
          <div className="review-list-head">
            <h2>棋谱复盘</h2>
            <button className="review-btn ghost" onClick={onBack}>
              ← 返回大厅
            </button>
          </div>

          {error && <div className="review-error">{error}</div>}
          {loading && <div className="review-loading">加载中...</div>}

          {!loading && games && games.length === 0 && !error && (
            <div className="review-empty">暂无存档棋谱。结束一局对局后会自动存档。</div>
          )}

          {games && games.length > 0 && (
            <div className="review-table-wrap">
              <table className="review-table">
                <thead>
                  <tr>
                    <th>对局</th>
                    <th>结果</th>
                    <th>手数</th>
                    <th>棋盘</th>
                    <th>结束时间</th>
                  </tr>
                </thead>
                <tbody>
                  {games.map((g) => (
                    <tr key={g.filename} onClick={() => openGame(g)}>
                      <td>
                        <span className="rv-black-dot" /> {g.black} vs {g.white}
                        <span className="rv-white-dot" />
                      </td>
                      <td className="rv-result">{g.result}</td>
                      <td>{g.movesCount}</td>
                      <td>{g.boardSize} 路</td>
                      <td>{new Date(g.endedAt).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ) : (
        <div className="review-board-panel">
          <div className="review-board-head">
            <button className="review-btn ghost" onClick={backToList}>
              ← 棋谱列表
            </button>
            <div className="review-title">
              {selected.black} vs {selected.white} · 结果 {selected.result}
            </div>
            <button className="review-btn ghost" onClick={onBack}>
              退出复盘
            </button>
          </div>

          {error && <div className="review-error">{error}</div>}
          {loading && <div className="review-loading">加载中...</div>}

          {!loading && snapshots.length > 0 && (
            <div className="review-body">
              <div className="review-board-side">
                <div className="review-mode-bar">
                  <span className="mode-chip">{deadMode ? '标记死子中' : modeLabel[mode]}</span>
                  <span className="move-indicator">
                    {lastColor ? `上一手：${lastColor}` : '空盘'} ·{' '}
                    {nextColor ? `下一手：${nextColor}` : '已到终局'}
                  </span>
                </div>

                <GoBoard
                  size={boardSize}
                  stones={displayBoard}
                  onMove={handleBoardClick}
                  disabled={mode === 'replay' && !deadMode}
                  showCoordinates={true}
                  highlightLastMove={true}
                  lastMove={lastMove}
                  showHover={mode !== 'replay' || deadMode}
                  marks={marks}
                  deadStones={deadStones}
                  fill={true}
                />

                <div className="review-nav">
                  <button className="review-btn" onClick={() => goto(0)} disabled={currentIndex === 0}>
                    ⏮ 回起点
                  </button>
                  <button className="review-btn" onClick={() => goto(currentIndex - 1)} disabled={currentIndex === 0}>
                    ◀ 上一步
                  </button>
                  <div className="move-slider">
                    <input
                      type="range"
                      min={0}
                      max={snapshots.length - 1}
                      value={currentIndex}
                      onChange={(e) => goto(Number(e.target.value))}
                    />
                    <span className="move-num">
                      第 {currentIndex} / {totalMoves} 手
                    </span>
                  </div>
                  <button
                    className="review-btn"
                    onClick={() => goto(currentIndex + 1)}
                    disabled={currentIndex >= totalMoves}
                  >
                    下一步 ▶
                  </button>
                  <button className="review-btn" onClick={() => goto(totalMoves)} disabled={currentIndex >= totalMoves}>
                    到终点 ⏭
                  </button>
                </div>
              </div>

              <div className="review-tools">
                <div className="tool-group">
                  <h3>试下（变化分支）</h3>
                  <p className="tool-hint">从当前局面出发走变化，引擎判定合法性，可随时回主线。</p>
                  {mode !== 'try' ? (
                    <button className="review-btn primary" onClick={enterTry}>
                      ✏️ 开始试下
                    </button>
                  ) : (
                    <>
                      <button className="review-btn danger" onClick={exitTry}>
                        ↩ 回主线
                      </button>
                      {tryMsg && <div className="review-error small">{tryMsg}</div>}
                    </>
                  )}
                </div>

                <div className="tool-group">
                  <h3>标记（讲解标记）</h3>
                  <div className="mark-type-row">
                    {(['circle', 'triangle', 'cross', 'square', 'letter'] as MarkType[]).map((t) => (
                      <button
                        key={t}
                        className={`mark-btn ${markType === t ? 'active' : ''}`}
                        onClick={() => {
                          setMarkType(t);
                          // 清掉试下分支（若有），进入标记模式
                          branchGameRef.current = null;
                          setBranchBoard(null);
                          setBranchLastMove(null);
                          setTryMsg('');
                          setMode('mark');
                        }}
                      >
                        {t === 'circle' ? '○ 圆圈' : t === 'triangle' ? '△ 三角' : t === 'cross' ? '✕ 叉' : t === 'square' ? '□ 方块' : 'A 字母'}
                      </button>
                    ))}
                  </div>
                  {markType === 'letter' && (
                    <div className="letter-row">
                      <label>字母</label>
                      <input
                        type="text"
                        maxLength={1}
                        value={markLetter}
                        onChange={(e) => setMarkLetter(e.target.value.toUpperCase() || 'A')}
                      />
                    </div>
                  )}
                  <div className="mark-actions">
                    {mode === 'mark' ? (
                      <button className="review-btn danger" onClick={() => setMode('replay')}>
                        完成标记
                      </button>
                    ) : (
                      <span className="tool-hint">点上方类型后即可在棋盘放置</span>
                    )}
                    <button
                      className="review-btn ghost"
                      onClick={() => setMarks([])}
                      disabled={marks.length === 0}
                    >
                      清除全部标记
                    </button>
                  </div>
                  <div className="mark-count">已放置 {marks.length} 个标记</div>
                  <button
                    className="review-btn primary"
                    onClick={saveMarks}
                    disabled={marks.length === 0}
                  >
                    💾 保存标记到棋谱
                  </button>
                </div>

                <div className="tool-group">
                  <h3>形势判断与死子标记</h3>
                  <p className="tool-hint">数子法（子 + 领地）。终局前先标死子再数，修正胜负误判。</p>
                  <button className="review-btn primary" onClick={handleScore}>
                    📊 判断当前形势
                  </button>
                  <button className="review-btn" onClick={autoMarkDead}>
                    ✨ 自动识别死子
                  </button>
                  {deadMode ? (
                    <button className="review-btn danger" onClick={() => setDeadMode(false)}>
                      ✔ 完成死子标记
                    </button>
                  ) : (
                    <button
                      className="review-btn"
                      onClick={() => {
                        setDeadMode(true);
                        setMode('mark');
                        branchGameRef.current = null;
                        setBranchBoard(null);
                        setBranchLastMove(null);
                        setTryMsg('');
                      }}
                    >
                      ⚑ 标记死子
                    </button>
                  )}
                  <div className="mark-count">已标记死子 {deadStones.length} 个（红色圈）</div>
                  {score && (
                    <div className="score-result">
                      <div className="score-row">
                        <span className="rv-black-dot" /> 黑方：<b>{score.black}</b> 子
                      </div>
                      <div className="score-row">
                        <span className="rv-white-dot" /> 白方：<b>{score.white}</b> 子
                      </div>
                      <div className="score-lead">
                        {(() => {
                          const diff = score.black - score.white - komi / 2;
                          return diff > 0
                            ? `黑方领先 ${diff.toFixed(2)} 子`
                            : diff < 0
                              ? `白方领先 ${Math.abs(diff).toFixed(2)} 子`
                              : '双方持平';
                        })()}
                      </div>
                      <div className="score-note">
                        数子法（子+领地）· 贴目 {komi} 目{deadStones.length > 0 ? ` · 已剔除死子 ${deadStones.length} 个` : ''}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
